import React from "react";
import { Icons } from "../common/DashboardComponents";
import StatusBadge from "../common/StatusBadge";

// Grant status definitions
export const GRANT_STATUS = {
  APPLIED: "10", // Grant Applied By ESM
  ACCEPTED: "11", // Grant Accepted By Aresa Center & Forwarded
  REJECTED: "12", // Grant Rejected By Aresa Center
  IN_PROGRESS: "13", // Grant Approval in progress
  AWAITING_DOCUMENTS: "14", // Awaiting Documents from ESM
  DOCUMENT_RESUBMITTED: "15", // Document Resubmitted by ESM
  APPROVED: "16", // Grant Approved by Branch User
  REJECTED_FINAL: "17", // Grant Rejected By Branch User

  // Grouped statuses for filtering
  FORWARDED: ["11", "13", "14", "15", "16", "17"], // All forwarded statuses
  PENDING: ["11", "13", "14", "15"], // All pending statuses
};

// Status options for filtering based on role
export const getStatusOptions = (role, grants) => {
  // Common options available to all roles
  const commonOptions = [{ value: "all", label: "All Status" }];

  // Get unique statuses from grants data
  const uniqueStatuses = [...new Set(grants.map(g => g.STATUS_NAME).filter(Boolean))];

  // Create status options from unique statuses
  const statusOptions = uniqueStatuses.map(status => ({
    value: status,
    label: status
  }));

  return [...commonOptions, ...statusOptions];
};

// Get schemes options from master grants or grants data
export const getSchemeOptions = (grants) => {
  if (!grants || !Array.isArray(grants)) {
    return [];
  }

  // Extract unique schemes
  const uniqueSchemes = [
    ...new Set(grants.map((g) => g.GRANT_TYPE).filter(Boolean)),
  ];

  return [
    { value: "all", label: "All Schemes" },
    ...uniqueSchemes.map((scheme) => ({
      value: scheme,
      label: scheme,
    })),
  ];
};

// Helper function to get filtered grants based on role
export const getFilteredGrants = (role, grants) => {
  switch (role) {
    case 2: // ARESA Center - show all grants
      return grants;
    case 3: // Branch - show only forwarded grants
      return grants;
      // grants.filter((g) => GRANT_STATUS.FORWARDED.includes(g.STATUS_ID));

    case 4: // DGAR - show all grants
    case 5: // COLA - show all grants
    case 6:
    case 7:
    case 8:
      return grants;
    default:
      return grants;
  }
};

// Get stats based on role and grants
export const getGrantStats = (role, grants) => {
  // Filter grants based on role
  const filteredGrants = getFilteredGrants(role, grants);

  // Calculate common stats
  const countGrantsByStatus = (statusIds) => {
    return filteredGrants.filter(
      (g) => g.STATUS_ID && statusIds.includes(g.STATUS_ID)
    ).length;
  };

  const totalGrants = filteredGrants.length;
  const pendingGrants = countGrantsByStatus(GRANT_STATUS.PENDING);
  const approvedGrants = countGrantsByStatus([GRANT_STATUS.APPROVED]);
  const rejectedGrants = countGrantsByStatus([GRANT_STATUS.REJECTED_FINAL]);

  // Define stats cards based on role
  switch (role) {
    case 2: // ARESA Center
      return [
        {
          title: "Total Grants",
          value: totalGrants,
          icon: Icons.Grants,
          color: "#9C27B0",
          additionalInfo: `Pending: ${pendingGrants} | Approved: ${approvedGrants} | Rejected: ${rejectedGrants}`,
        },
      ];
    case 3: // Branch
      return [
        {
          title: "Forwarded Grants",
          value: totalGrants,
          icon: Icons.Grants,
          color: "#9C27B0",
          additionalInfo: `Pending: ${pendingGrants} | Approved: ${approvedGrants} | Rejected: ${rejectedGrants}`,
        },
      ];
    case 4: // DGAR
    case 5: // COLA
    case 6:
    case 7:
    case 8:
      return [
        {
          title: "Total Grants",
          value: totalGrants,
          icon: Icons.Grants,
          color: "#9C27B0",
          additionalInfo: `Pending: ${pendingGrants} | Approved: ${approvedGrants} | Rejected: ${rejectedGrants}`,
        },
      ];
    default:
      return [
        {
          title: "Grants",
          value: totalGrants,
          icon: Icons.Grants,
          color: "#9C27B0",
          additionalInfo: null,
        },
      ];
  }
};

// Get the title for the grant list based on role
export const getGrantListTitle = (role) => {
  switch (role) {
    case 2:
      return "All Grant Applications";
    case 3:
      return "Branch Grant Applications";
    case 4:
      return "DGAR Grant Applications";
    case 5:
      return "COLA Grant Applications";
    default:
      return "Grant List";
  }
};

// Get table columns based on role
export const getTableColumns = (role, navigate) => {
  // Common columns for all roles
  const commonColumns = [
    {
      header: "Grant ID",
      render: (grant) => `C${grant.APP_ID.toString().padStart(10, "0")}`,
    },
    {
      header: "Applicant Name",
      render: (grant) => grant.USER_NAME || "N/A",
    },
    {
      header: "Scheme",
      render: (grant) => grant.GRANT_TYPE,
    },
    {
      header: "Submission Date",
      render: (grant) => grant.ENTRY_DATETIME || "N/A",
    },
    {
      header: "Pending At",
      render: (grant) => grant?.PENDING_ROLE_NAME || "-",
    },
    {
      header: "Submission Date",
      render: (grant) => grant.ENTRY_DATETIME || "N/A",
    },
    {
      header: "Status",
      render: (grant) => (
        <StatusBadge status={grant.STATUS_NAME} isGrant={true} />
      ),
    },
    {
      header: "Actions",
      render: (grant) => (
        <button
          onClick={() => {
            navigate(`/GrantDetails`, {
              state: {
                id: grant.APP_ID,
                centerName: grant.UNIT_CENTRE_NAME || "N/A",
                esm: grant.ESM_ID || "N/A",
                grnatDetails: grant,
               
              },
            });
          }}
          className="btn btn-sm btn-primary"
        >
          View Details
        </button>
      ),
    },
  ];

  // Add role-specific columns or modify the common columns
  if (role === 2) {
    // ARESA Center
    return [
      ...commonColumns.slice(0, 2), // ID, Applicant
      {
        header: "Remarks",
        render: (grant) => (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: "200px" }}
          >
            {grant.REMARKS || "N/A"}
          </span>
        ),
      },
      ...commonColumns.slice(2), // Scheme, Date, Status, Actions
    ];
  } else if (role === 3) {
    // Branch
    return [
      ...commonColumns.slice(0, 2),
      {
        header: "Remarks",
        render: (grant) => grant.REMARKS || "N/A",
      },
      ...commonColumns.slice(2, 3), // Scheme
      {
        header: "Pension Amount",
        render: (grant) =>
          grant.PENSION_AMOUNT ? `₹${grant.PENSION_AMOUNT}` :grant.RELIEF_AMOUNT?`₹${grant.RELIEF_AMOUNT}`:"N/A",
      },
      ...commonColumns.slice(3),
    ];
    
  } else {
    // DGAR (4) or COLA (5)
    return [
      ...commonColumns.slice(0, 2),
      {
        header: "ARESA Center",
        render: (grant) => grant.UNIT_CENTRE_NAME || "N/A",
      },
      ...commonColumns.slice(2, 3), // Scheme
      {
        header: "Relief Amount",
        render: (grant) =>
          grant.RELIEF_AMOUNT ? `₹${grant.RELIEF_AMOUNT}` : "N/A",
      },
      {
        header: "Pension Amount",
        render: (grant) =>
          grant.PENSION_AMOUNT ? `₹${grant.PENSION_AMOUNT}` :grant.RELIEF_AMOUNT?`₹${grant.RELIEF_AMOUNT}`:"N/A",
      },
      ...commonColumns.slice(3), // Date, Status, Actions
    ];
  }
};
